import * as React from 'react';
import KNB from '@wmfe/mach-pro-knb';
import './index.css';
import { i18nClient } from '@sailor/i18n-mach-pro/lib';
import GuidePage from '@/src/pages/homepage/preheat/noservice';
import CommonPage from '@/src/pages/homepage/preheat/common';
import Loading from '@/src/pages/homepage/preheat/loading';
import { isEqual } from 'lodash';
import { HOME_PAGE_ERROR_CODE, HomePageEmptyImgMapping } from '@/src/pages/homepage/home_page_main/utils/constant';
import { getLocale } from '@/utils/getLocale';
import { HomePageLoganType, logan } from '@/src/common/utils/Logan';
import { callModule } from '../../../shared_worker_global/market/util';
import * as LX from './request/lx';
import { PreheatMonitor } from './utils/monitor';
import { PollingManager } from './utils/PollingManager';

const WMRouter = Mach.requireModule('Router');
const EventCenter = Mach.requireModule('EventCenter');

interface State {
  styles: {
    pageWidth: number;
    pageHeight: number;
    bgiWidth: number;
    bgiHeight: number;
    titleTopMargin: number;
  };
  guideWord: {
    title: string;
    subTitle: string;
    guidePicUrl: string;
    button: string;
    exploreText: string;
    linkUrl: string;
    secondTitle: string;
    secondSubTitle: string;
    noServiceInfo: string; // 无服务文案
    benefitInfo: string; // 利益点文案
    buttonColor: string; // 按钮背景色
  };
  logined: boolean;
  height?: number;
  code: number;
  message: string;
  viewHeight?: number;
  nuclearInfo: {
    content: string;
    picUrl: string;
  };
}
interface Props {
  apiData?: any;
  minHeight: number;
}

/**
 * 预热页面
 * 201003221 已登录
 * 201003222 未登录
 */
const prePageLoginedCode = HOME_PAGE_ERROR_CODE.PRE_PAGE_LOGIN_CODE;
const prePageNonLoginCode = HOME_PAGE_ERROR_CODE.PRE_PAGE_NO_LOGIN_COE;

/**
 * 未开城
 * 201003216 已登录
 * 201003217 未登录
 */
const loginedCode = HOME_PAGE_ERROR_CODE.LOGIN_CODE;
const noLoginedCode = HOME_PAGE_ERROR_CODE.NO_LOGIN_CODE;

// 无供给
const noSupplyArea = HOME_PAGE_ERROR_CODE.NO_SUPPLY_AREA;
// 无数据
const noData = HOME_PAGE_ERROR_CODE.NO_DATA;

const SLUserAddress = Mach.requireModule('SLUserAddress');
const SLLocation = Mach.requireModule('SLLocation');

export default class Preheat extends React.Component<Props, State> {
  guideInfo?: any;
  guideWord?: any
  nuclearInfo?: any
  code: number = HOME_PAGE_ERROR_CODE.NO_DATA;
  message?: string;
  viewRef: React.RefObject<any> = React.createRef();
  secondViewRef: React.RefObject<any> = React.createRef();
  viewLayoutRef: React.RefObject<any> = React.createRef();
  topMargin = 10;
  goContanerMarginTop = 48;
  lastLocale: string | null
  loginHeight = 80;
  topOffset = 10;
  unLoginOffset = 12 + this.topOffset;
  loginOffset = 28 + this.topOffset;
  monitor = new PreheatMonitor();
  // 预热倒计时-剩余时间
  currentRemainTime = -1;

  constructor(props: Props) {
    super(props);
    const pageWidth = Mach.env?.screenWidth || 375;
    const pageHeight = Math.ceil(Mach.env?.screenHeight - Mach.env?.softMenuBarHeight);
    // 按比例缩放
    const bgiHeight = (412 / 375) * pageWidth;
    const titleTopMargin = bgiHeight / 10;

    this.state = {
      styles: {
        pageWidth,
        pageHeight,
        bgiWidth: pageWidth,
        bgiHeight,
        titleTopMargin,
      },
      guideWord: null as any,
      logined: false,
      message: '',
      code: 0,
    };

    this.parseData();

    Mach.on('dataChanged', () => {
      this.parseData();
    });
    this.lastLocale = getLocale();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    const locale = getLocale();

    if (!isEqual(this.props.apiData, prevProps.apiData)) { // API数据改变则刷新，不再依据sceneCode
      this.clearCountDownId(); // 重置计时器
      this.parseData();
    } else if (this.lastLocale != locale) {
      this.lastLocale = locale;
      this.parseData();
    }
  }

  componentDidMount() {
    //前后台切换时，暂停或恢复轮询
    Mach.on('pageWillDisappear', () => {
      PollingManager.getInstance()?.pause();
    });
    Mach.on('pageDidAppear', () => {
      PollingManager.getInstance()?.resume();
      PollingManager.forceRefreshIfRunning();
    });
  }

  componentWillUnmount() {
    this.clearCountDownId(); // 重置计时器
    if (PollingManager.getInstance()?.isRunning()) {
      this.monitor.reportNuclearPollingStatus(2);
    }
    PollingManager.destroyInstance(); //销毁轮询
  }

  parseData() {
    const data = this.props.apiData?.json_data ? this.props.apiData?.json_data : Mach.data;
    if (!data) {
      return;
    }
    const {
      code, sceneCode, message, guideInfo, guideWord, countDownSeconds, nuclearInfo
    } = data;
    this.updateState(code ?? sceneCode, guideInfo, guideWord, message, countDownSeconds, nuclearInfo);
  }

  reportMV = (logined) => {
    LX.ggMV(logined);
    if (!logined) {
      LX.loginMV();
    }
  };

  updateState = (code, guideInfo, guideWord, message, countDownSeconds, nuclearInfo) => {
    const logined = code === prePageLoginedCode || code === loginedCode;
    if (this.code !== code) {
      this.code = code;
    }

    // 预热页埋点根据接口刷新上报
    if (this.isPrePage()) {
      LX.preheatLoginMV(); // 预热MV
      this.reportMV(logined); // 按钮相关MV
    }

    this.guideInfo = guideInfo;
    this.guideWord = guideWord;
    this.message = message;
    this.code = code;

    if (guideWord) {
      this.setState({
        guideWord: {
          ...guideWord,
          guidePicUrl: guideWord?.guidePicUrl,
        },
        logined,
        code,
        message
      });
    } else {
      this.setState({
        code,
        logined,
        message
      });
    }
    //核按钮页面
    if (code === HOME_PAGE_ERROR_CODE.NUCLEAR_BUTTON) {
      LX.nuclearButtonMV();
      //避免重复创建
      logan(HomePageLoganType.PreHeat, {
        msg: 'NUCLEAR_BUTTON START',
      });
      PollingManager.createInstance().reset();
      this.monitor.reportNuclearPollingStatus(1);
      this.setState({ nuclearInfo });
    } else {
      PollingManager.destroyInstance();
      this.monitor.reportNuclearPollingStatus(2);
    }
    // 如当前是预热页 && 倒计时 > 0
    if (this.isPrePage() && countDownSeconds && countDownSeconds > 0) {
      this.currentRemainTime = countDownSeconds;
      this.startCountDown(); // 开启计时器
      this.monitor.reportCountDownSecondsStatus(1);
    } else {
      this.clearCountDownId(); // 重置计时器
      this.monitor.reportCountDownSecondsStatus(0);
    }

    // 监控打点
    this.reportRender();
  }

  /**
   * 监控打点
   */
  reportRender = () => {
    this.monitor.reportRender(this.code);
    const isRenderSuccess = this.code === prePageLoginedCode || this.code === prePageNonLoginCode || this.code === loginedCode || this.code === noLoginedCode || this.code === HOME_PAGE_ERROR_CODE.NO_LOGIN_CODE;
    if (isRenderSuccess) {
      this.monitor.reportRenderSuccess(this.code);
    } else {
      this.monitor.reportRenderError(this.code);
    }
  }

  handleLogin = () => {
    KNB && KNB?.login({ success(res) {}, fail(err) {} });
  };

  onClickGoButton = () => {
    this.monitor.reportClickLogin(this.code);
    if (this.state.logined) {
      this.onClickGoView();
      return;
    }
    LX.loginMC();
    this.handleLogin();
  };

  getGoNewHornConfig = () => {
    const Horn = Mach.requireModule('SLCHorn');
    const config = Horn?.getAppConfig?.() || {};
    return typeof (config.isNewAddressPopup) !== 'boolean' || config.isNewAddressPopup;
  }

  onClickGoView = () => {
    this.monitor.reportClickExplore(this.code);
    LX.ggMC(this.state.logined);
    // 默认兜底url
    let targetUrl;

    const isNew = this.getGoNewHornConfig();
    if (isNew) {
      const bundleName = 'mach_pro_sailor_c_address_select_popup';
      let cid = this.props.apiData.json_data?.cid;
      if (!cid) {
        switch (Mach.env.bundleName) {
          case 'mach_pro_sailor_c_pick_up_page_main':
            cid = 'c_sailor_c_m5n64ar9';
            break;
          default:
            break;
        }
      }
      const data: any = {
        scene: 5,
        cid,
      };
      const jsonData = encodeURIComponent(JSON.stringify(data));
      targetUrl = `sailorc://keeta.com/machproalert?bundle_name=${bundleName}&data=${jsonData}`;
    } else {
      const { guideInfo } = this;
      if (!guideInfo || !guideInfo.areaInfos) {
        SLUserAddress?.didSelectLocation(SLLocation.defaultLocation());
        return;
      }
      const encodeData = encodeURIComponent(JSON.stringify(this.guideInfo));
      targetUrl = `sailorc://keeta.com/machproalert?bundle_name=mach_pro_sailor_c_service_address_popup&data=${encodeData}`;
    }

    callModule(
      'Router',
      'navigateTo',
      (result) => {
        if (!result.success) {
          console.log('navigate error: c_area_preheat ~~~');
        }
      },
      targetUrl,
      {}
    );
  };

  onClickImageToPage = (linkUrl?: string) => {
    LX.preheatLoginMC();
    if (!linkUrl) {
      return;
    }
    // 下发地址的格式需要以  sailorc:// 开头
    WMRouter.navigateTo(linkUrl);
  }

  onSecondLayout = () => {
    const height = this.secondViewRef.current?.measureInWindow()?.height;
    this.setState({
      viewHeight: height
    });
  }

  renderUnloginView = (guideWord: any) => (
    <view className="second-op-view" ref={this.secondViewRef}>
      {guideWord?.button && (
        <text
          className="go-button"
          style={{ backgroundColor: guideWord?.buttonColor || '#FFEA00' }}
          content={guideWord.button}
          onClick={this.onClickGoButton}
        />
      )}
      {guideWord?.exploreText && (
        <view className="gg-view" onClick={this.onClickGoView}>
          <text className="gg-text" content={guideWord.exploreText} />
          <image className="gg-icon" src="assets://preheat/icon_arrow_right.png" autoRTL />
        </view>
      )}
    </view>
  )

  renderLoginView = (guideWord: any) => (
    <view className="second-op-view">
      {guideWord?.button && (
        <text
          className="go-button"
          style={{ backgroundColor: guideWord?.buttonColor || '#FFEA00' }}
          content={guideWord?.button}
          onClick={this.onClickGoView}
        />
      )}
    </view>
  )

  renderPrePage = () => {
    const {
      guideWord, styles, logined, height
    } = this.state;
    if (!guideWord) {
      logan(HomePageLoganType.PreHeat, {
        msg: 'pre_page_guide_word_is_null',
        logined
      });
      return null;
    }

    let minHeight = this.props.minHeight;
    if (minHeight < 0) {
      minHeight = height ?? 0;
    }

    const bgiWidth = Mach.env?.screenWidth || 375;
    const bgHeight = (412 / 375) * bgiWidth;

    return (
      <view className="page-preheat" style={{ height: this.props.minHeight }}>
        <image
          src={guideWord.guidePicUrl}
          className="preheat-img"
          resizeMode="aspectFit"
          placeholder="assets://preheat/keeta_placeholder.png"
          style={{
            width: bgiWidth,
            height: bgHeight,
          }}
          onClick={() => this.onClickImageToPage(guideWord.linkUrl)}
        />

        {/* 不区分登录和未登录状态，只要配置了 title 和 subTitle 就会展示 */}
        {(guideWord.title || guideWord.subTitle) && (
          <view className="title-view" style={{ top: styles.titleTopMargin }}>
            {guideWord.title && <text className="title-text" content={guideWord.title} />}
            {guideWord.subTitle && <text className="subtitle-text" content={guideWord.subTitle} />}
          </view>
        )}

        {/* 主图下新增文案区 */}
        <view className="below-img-text-view">
          {/* 无服务文案 */}
          {guideWord?.noServiceInfo && <text className="no-service-info-text" content={guideWord?.noServiceInfo} />}
          {/* 利益点文案 */}
          {guideWord?.benefitInfo && <text className="benefit-info-text" content={guideWord?.benefitInfo} />}
        </view>

        {!logined ? this.renderUnloginView(guideWord) : this.renderLoginView(guideWord)}
      </view>
    );
  }

  renderNuclearPage = () => {
    const { nuclearInfo } = this.state;
    const bgiWidth = Mach.env?.screenWidth || 375;
    const bgHeight = (250 / 375) * bgiWidth;

    return (
      <view className="page-preheat" style={{ height: this.props.minHeight }}>
        <image
          src={nuclearInfo.picUrl}
          className="preheat-img"
          resizeMode="aspectFit"
          placeholder="assets://preheat/nuclear_placeholder.png"
          style={{
            width: bgiWidth,
            height: bgHeight,
          }}
        />
        {nuclearInfo?.content && <text className="nuclear-text" content={nuclearInfo?.content} />}
      </view>
    );
  }

  isPrePage = () => {
    const result = this.code === prePageLoginedCode || this.code === prePageNonLoginCode;
    return result;
  }

  isNoService = () => {
    const result = this.code === loginedCode || this.code === noLoginedCode;
    return result;
  }

  //核按钮状态
  isNuclearRender = () => {
    const result = this.code === HOME_PAGE_ERROR_CODE.NUCLEAR_BUTTON;
    return result;
  }

  onLayout = () => {
    const height = this.viewRef.current?.measureInWindow()?.height;
    this.setState({
      height
    });
  }

  /**
   * 开启倒计时任务（间隔30秒判断是否符合条件）
   */
  startCountDown = () => {
    // 重置计时器（这里需要多重置一次，否则旧的定时器无法清除）
    this.clearCountDownId();
    // 开启定时器
    globalThis.currentCountDownIntervalId = setInterval(() => {
      this.currentRemainTime -= 30;
      if (this.currentRemainTime <= 0) {
        // 重置变量
        this.currentRemainTime = -1;
        // 发送首页刷新事件
        EventCenter?.emit('refresh_all_homepage');
        // 监控打点
        this.monitor.reportCountDownSecondsRefresh();
        // 重置计时器
        this.clearCountDownId();
      }
    }, 30 * 1000);
  }

  /**
   * 重置计时器
   */
  clearCountDownId = () => {
    if (globalThis.currentCountDownIntervalId) {
      clearInterval(globalThis.currentCountDownIntervalId);
    }
  };

  render() {
    let { height, code, message } = this.state;
    let minHeight = this.props.minHeight;
    if (minHeight < 0) {
      minHeight = height ?? 0;
    }
    const imageUrl = HomePageEmptyImgMapping[code];
    const data = this.props.apiData?.json_data ? this.props.apiData?.json_data : Mach.data;
    LX.updateCid(this.props.apiData?.log_info?.cid); // 更新本地cid（使用logData中取出的cid）
    if (data) {
      message = data.message;
    }
    return (
      <view ref={this.viewRef} style={{ minHeight }} onLayout={() => this.onLayout()}>
        {
          this.isNuclearRender() ? (this.renderNuclearPage())
            : this.isPrePage() ? (this.renderPrePage())
              : (this.isNoService() ? (
                <GuidePage apiData={this.props.apiData} nativeData={Mach.data} height={minHeight} />
              ) : (
                this.code === HOME_PAGE_ERROR_CODE.LOADING
                  ? (
                    <Loading
                      code={this.code}
                    />
                  )
                  : (
                    <CommonPage// 无供给页、筛选无数据页面处理逻辑同、其他 code 兜底页面
                      data={{
                        imageUrl: imageUrl ?? '',
                        code,
                        message,
                        btnText: this.guideWord?.button ?? i18nClient.getText('basic_address_refreshbutton')
                      }}
                      guideWord={this.guideWord}
                    />
                  )
              )
              )
        }
      </view>
    );
  }
}
